import { useState, useRef } from "react";
import api from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";

export default function PhotoUploader({ images, onChange, max = 8 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const pick = () => inputRef.current?.click();

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setErr(""); setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        if (images.length + uploaded.length >= max) break;
        if (file.size > 8 * 1024 * 1024) { setErr(`${file.name} is over 8MB`); continue; }
        const form = new FormData();
        form.append("file", file);
        const r = await api.post("/uploads/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.url);
      }
      onChange([...images, ...uploaded]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div data-testid="photo-uploader">
      <div className="grid grid-cols-3 gap-3">
        {images.map((src, i) => (
          <div key={i} className="relative group">
            <img src={src} alt="" className="w-full h-32 object-cover rounded-xl border border-slate-200"/>
            <button type="button" onClick={() => remove(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-slate-200 grid place-items-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`photo-remove-${i}`} aria-label="Remove photo">
              <X size={14}/>
            </button>
          </div>
        ))}
        {images.length < max && (
          <button type="button" onClick={pick} disabled={uploading}
            className="h-32 border-2 border-dashed border-slate-300 rounded-xl grid place-items-center text-slate-500 hover:border-[var(--fpn-navy)] hover:text-[var(--fpn-navy)] transition-colors"
            data-testid="photo-upload-button">
            {uploading ? <Loader2 className="animate-spin" size={22}/> : (
              <div className="text-center">
                <Upload size={22} className="mx-auto"/>
                <div className="text-xs mt-2 font-medium">Add photo</div>
              </div>
            )}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid="photo-file-input"/>
      {err && <div className="text-sm text-red-600 mt-3" data-testid="photo-upload-error">{err}</div>}
      <p className="text-xs text-slate-500 mt-3">Upload {max} photos max · JPG, PNG or WEBP · under 8MB each · first photo is the cover image.</p>
    </div>
  );
}
